import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { Search, RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified API base

const ManageRides = () => {
  const [rides, setRides] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // ============================================================
  // 📡 Fetch All Rides
  // ============================================================
  const fetchRides = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/rides`);
      setRides(res.data || []);
    } catch (err) {
      console.error("❌ Failed to load rides:", err);
      toast.error("Failed to load rides");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRides();
  }, []);

  // ============================================================
  // 🔍 Search Filter
  // ============================================================
  const filtered = rides.filter((r) =>
    [r.from, r.to, r.workerId?.name, r.bookedBy?.name]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  // ============================================================
  // 🖥️ Render
  // ============================================================
  return (
    <div className="flex font-[Poppins]">
      <Sidebar />

      <main className="ml-0 md:ml-64 w-full bg-gray-100 min-h-screen p-6 md:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-extrabold text-gray-800">
            🚗 Manage Rides
          </h1>
          <button
            onClick={fetchRides}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-70"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex items-center mb-6">
          <Search className="w-5 h-5 text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Search rides by city, driver, or customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-md border rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-gray-500">Loading rides...</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500">No rides found.</p>
        ) : (
          <div className="overflow-x-auto bg-white rounded-2xl shadow">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700 border-b">
                <tr>
                  <th className="p-3 text-left">From → To</th>
                  <th className="p-3 text-left">Date & Time</th>
                  <th className="p-3 text-left">Driver</th>
                  <th className="p-3 text-left">Customer</th>
                  <th className="p-3 text-left">Price</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Payout</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ride) => (
                  <tr
                    key={ride._id}
                    className="border-t hover:bg-gray-50 transition duration-150"
                  >
                    <td className="p-3 font-semibold text-gray-800">
                      {ride.from} → {ride.to}
                    </td>
                    <td className="p-3">
                      {ride.date} {ride.time}
                    </td>
                    <td className="p-3">{ride.workerId?.name || "—"}</td>
                    <td className="p-3">{ride.bookedBy?.name || "—"}</td>
                    <td className="p-3 font-medium text-indigo-700">
                      ${ride.price?.toFixed(2)}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          ride.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : ride.status === "cancelled"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {ride.status}
                      </span>
                    </td>
                    <td className="p-3">
                      {ride.isPaidOut ? (
                        <span className="text-green-600 font-semibold">
                          ✅ Released
                        </span>
                      ) : (
                        <span className="text-gray-500">On Hold</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageRides;
